'use client'

import { motion } from 'framer-motion'
import { Bot, Store, ArrowRight } from 'lucide-react'
import { Container } from '@/components/ui/container'
import { Section } from '@/components/ui/section'
import Link from 'next/link'

const crossSells = [
  {
    name: 'Buddy AI Assistant',
    description: 'Add AI sales, support, and conversion to your store so more visitors convert after they discover your partner products.',
    href: '/buddy-ai-assistant',
    icon: Bot,
    theme: 'ai' as const,
  },
  {
    name: 'Marketplace',
    description: 'List your products on the Buddify Marketplace and reach deal-hunting shoppers looking for independent brands.',
    href: '/marketplace',
    icon: Store,
    theme: 'marketplace' as const,
  },
]

const themeStyles = {
  ai: {
    card: 'bg-panel border-line hover:border-green-500/30',
    icon: 'bg-raised text-brand-green',
    title: 'text-ink',
    body: 'text-muted',
    link: 'text-brand-green',
  },
  marketplace: {
    card: 'bg-panel border-line hover:border-brand-blue-line',
    icon: 'bg-brand-blue-soft text-brand-blue',
    title: 'text-ink',
    body: 'text-muted',
    link: 'text-brand-blue',
  },
}

export function BrandPartnershipsCrossSellSection() {
  return (
    <Section background="gray">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <p className="text-micro-uppercase text-brand-green mb-3">GROW FURTHER</p>
          <h2 className="text-display-1 text-ink mb-3">
            Unlock the Full Buddify Stack
          </h2>
          <p className="text-body-lg text-muted max-w-2xl mx-auto">
            Brand Partnerships expands your catalog. Pair it with AI and Marketplace to turn that catalog into revenue across every channel.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {crossSells.map((item, index) => {
            const theme = themeStyles[item.theme]
            const Icon = item.icon

            return (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  href={item.href}
                  className={`group block h-full rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${theme.card}`}
                >
                  <div className={`inline-flex p-3 rounded-xl mb-4 ${theme.icon}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={`text-h4 mb-2 ${theme.title}`}>{item.name}</h3>
                  <p className={`text-body mb-5 ${theme.body}`}>{item.description}</p>
                  <span className={`inline-flex items-center gap-1 text-sm font-semibold ${theme.link}`}>
                    Learn more
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </motion.div>
            )
          })}
        </div>
      </Container>
    </Section>
  )
}
